import dynamic from 'next/dynamic'
import Image from 'next/image'
import {useEffect, useRef, useState} from 'react'
import {Icon} from 'src/common/Icon'

const VideoPlayer = dynamic({
  loader: () => import('src/common/VideoPlayer').then((mod) => mod.VideoPlayer),
  ssr: false,
})

type VideoCoverProps = {
  src: string
  mainImage?: {url: string}
  companyLogo?: {url: string}
  quoteText?: string
  quoteAuthorName?: string
  quoteAuthorRole?: string
}

export const VideoCover = ({
  src,
  mainImage,
  companyLogo,
  quoteText,
  quoteAuthorName,
  quoteAuthorRole,
}: VideoCoverProps) => {
  const [isPlaying, setIsPlaying] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isFadingOut, setIsFadingOut] = useState(false)
  const loadingRef = useRef<HTMLDivElement>(null)

  const handlePlay = () => {
    setIsFadingOut(true)

    setTimeout(() => {
      setIsLoading(true)
      setIsPlaying(true)
      setIsFadingOut(false)
    }, 400)
  }

  // Hide loading overlay once the iframe is ready
  useEffect(() => {
    if (!isLoading || !loadingRef.current) {
      return
    }

    const parent = loadingRef.current.parentElement

    if (!parent) {
      return
    }

    const attachLoadListener = (iframe: HTMLIFrameElement) => {
      if (iframe.contentDocument?.readyState === 'complete') {
        setIsLoading(false)

        return
      }

      iframe.addEventListener('load', () => setIsLoading(false), {once: true})
    }

    const existingIframe = parent.querySelector('iframe')

    if (existingIframe) {
      attachLoadListener(existingIframe)

      return
    }

    const observer = new MutationObserver(() => {
      const iframe = parent.querySelector('iframe')

      if (iframe) {
        attachLoadListener(iframe)
        observer.disconnect()
      }
    })

    observer.observe(parent, {childList: true, subtree: true})

    return () => observer.disconnect()
  }, [isLoading])

  const hasImage = mainImage && 'url' in mainImage && mainImage.url
  const hasLogo = companyLogo && 'url' in companyLogo && companyLogo.url
  const truncatedQuote = quoteText ? quoteText.split(' ').slice(0, 7).join(' ') + '...' : null
  const shortRole = quoteAuthorRole?.split(',')[0]

  return (
    <div className="relative aspect-video w-full overflow-hidden rounded-lg">
      {isPlaying ? (
        <>
          {isLoading && (
            <div ref={loadingRef} className="absolute inset-0 z-10 flex items-center justify-center bg-green-dark">
              {hasImage && <Image src={mainImage!.url} alt="" fill className="object-cover opacity-20" />}
              <div className="relative size-14 animate-pulse rounded-full bg-green-neon/60 lg:size-16">
                <Icon
                  name="play"
                  className="absolute left-1/2 top-1/2 size-6 -translate-x-1/2 -translate-y-1/2 text-green-dark lg:size-7"
                />
              </div>
            </div>
          )}
          <VideoPlayer src={src} aspectRatio="aspect-video" autoplay hideControls={false} startMuted={false} />
        </>
      ) : (
        <button
          className="group/cover relative flex size-full cursor-pointer items-center justify-center bg-green-dark"
          onClick={handlePlay}
        >
          {hasImage && <Image src={mainImage!.url} alt="" fill className="object-cover opacity-20" />}

          <div className="relative z-10 flex size-full flex-col justify-between p-6 lg:p-8">
            {/* Top — customer logo left, Moss icon right */}
            <div
              className={`flex items-center justify-between transition-opacity duration-300 ${isFadingOut ? 'opacity-0' : 'opacity-100'}`}
            >
              {hasLogo && (
                <Image
                  src={companyLogo!.url}
                  alt=""
                  width={120}
                  height={28}
                  className="h-6 w-auto object-contain lg:h-7"
                />
              )}
              <Image src="/icons/moss-icon-white.svg" alt="Moss" width={28} height={28} className="h-6 w-auto lg:h-7" />
            </div>

            {/* Centre — play button */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="relative size-14 rounded-full bg-green-neon shadow-[0_0_30px_rgba(163,255,86,0.4)] transition-shadow duration-150 group-hover/cover:shadow-[0_0_60px_rgba(163,255,86,0.6)] lg:size-16">
                <Icon
                  name="play"
                  className="absolute left-1/2 top-1/2 size-6 -translate-x-1/2 -translate-y-1/2 text-green-dark lg:size-7"
                />
              </div>
            </div>

            {/* Spacer */}
            <div className="flex-1" />

            {/* Bottom — quote + author */}
            <div
              className={`flex flex-col gap-3 text-left transition-opacity duration-300 ${isFadingOut ? 'opacity-0' : 'opacity-100'}`}
            >
              {truncatedQuote && (
                <p className="max-w-[600px] text-18 font-medium leading-7 text-moss-white max-md:hidden lg:text-20 lg:leading-8">
                  {truncatedQuote}
                </p>
              )}
              {quoteAuthorName && (
                <div className="flex items-baseline gap-2">
                  <p className="text-16 font-medium text-moss-white">{quoteAuthorName}</p>
                  {shortRole && <p className="text-14 font-medium text-moss-white/70">{shortRole}</p>}
                </div>
              )}
            </div>
          </div>
        </button>
      )}
    </div>
  )
}
